import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StandardModal, { useModalLoading } from '../modales/StandardModal';
import { currentLang, useT } from '../../i18n';
import { useCompanyBrand } from '../../utils/company-brand';

/**
 * Reproductor privado de los videos de ayuda por pantalla (`GET help/videos/<pantalla>`).
 *
 *   - Pide un enlace firmado (vence en 5 minutos) cada vez que se abre; si vence mientras se ve, lo vuelve
 *     a pedir y sigue en el mismo segundo.
 *   - Sin botón de descarga, sin menú contextual, sin imagen en imagen y con la pista de subtítulos (VTT)
 *     del idioma de la persona.
 *   - Marca de agua con el usuario, la empresa y la fecha, que cambia de lugar cada pocos segundos.
 *   - Pantalla completa propia (del contenedor y no del <video>) para que la marca de agua siga encima.
 */

export interface ScreenVideoLinks {
  screen: string;
  lang: string;
  title: string;
  duration: number;
  video: { url: string; expiresAt: string };
  captions: { url: string; expiresAt: string; lang: string } | null;
}

/** Lista de videos disponibles (`GET help/videos`), una vez por idioma y sesión de la página. */
let listCache: { lang: string; promise: Promise<Set<string>> } | null = null;
export const loadAvailableVideos = (): Promise<Set<string>> => {
  const lang = currentLang();
  if (!listCache || listCache.lang !== lang) {
    const promise = axios
      .get<{ enabled: boolean; data: { screen: string }[] }>('help/videos', { params: { lang } })
      .then(({ data }) => new Set((data?.enabled && Array.isArray(data.data) ? data.data : []).map((v) => v.screen)))
      .catch(() => {
        listCache = null; // reintenta la próxima vez
        return new Set<string>();
      });
    listCache = { lang, promise };
  }
  return listCache.promise;
};

/** ¿La pantalla (`offices`, `loan-payments`…) tiene video en el idioma de la persona? */
export const useScreenVideoAvailable = (screen: string | null) => {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let alive = true;
    setAvailable(false);
    if (screen) loadAvailableVideos().then((set) => alive && setAvailable(set.has(screen)));
    return () => {
      alive = false;
    };
  }, [screen]);
  return available;
};

const WATERMARK_MOVE_MS = 4000;
/** Posiciones (en % del video) por las que pasa la marca de agua, lejos de los controles. */
const SPOTS: [number, number][] = [
  [6, 8], [52, 12], [24, 34], [60, 42], [8, 58], [44, 66], [18, 20], [56, 26],
];

const Watermark: React.FC<{ text: string }> = ({ text }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setI((n) => (n + 1 + Math.floor(Math.random() * (SPOTS.length - 1))) % SPOTS.length), WATERMARK_MOVE_MS);
    return () => window.clearInterval(timer);
  }, []);
  const [left, top] = SPOTS[i];
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        left: `${left}%`,
        top: `${top}%`,
        maxWidth: '45%',
        pointerEvents: 'none',
        userSelect: 'none',
        // Sobre el video siempre: texto claro con sombra oscura, igual en modo claro y oscuro.
        color: 'common.white',
        opacity: 0.38,
        textShadow: '0 1px 2px rgba(0,0,0,.85)',
        fontSize: { xs: 11, sm: 13, xl: 15 },
        fontWeight: 600,
        lineHeight: 1.35,
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
        transition: 'left 1.2s ease, top 1.2s ease',
        zIndex: 2,
      }}
    >
      {text}
    </Box>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pantalla del video: la clave del recorrido sin `screen-` (`offices`). */
  screen: string;
}

const ScreenVideoPlayer: React.FC<Props> = ({ open, onClose, screen }) => {
  const t = useT();
  const brand = useCompanyBrand();
  const ml = useModalLoading();
  const { showLoading, hideLoading } = ml;
  const [links, setLinks] = useState<ScreenVideoLinks | null>(null);
  const [captionsUrl, setCaptionsUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'unavailable'>('loading');
  const [userName, setUserName] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [fullscreen, setFullscreen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const resumeAt = useRef<number | null>(null);
  const refreshing = useRef(false);

  const fetchLinks = useCallback(async () => {
    const { data } = await axios.get<ScreenVideoLinks>(`help/videos/${encodeURIComponent(screen)}`, { params: { lang: currentLang() } });
    setLinks(data);
    return data;
  }, [screen]);

  // Al abrir: enlace firmado nuevo, usuario para la marca de agua y subtítulos.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setState('loading');
    setLinks(null);
    setNow(new Date());
    showLoading();
    axios
      .get('user')
      .then(({ data }) => {
        const u = data?.data ?? {};
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.name || '';
        if (alive) setUserName(name ? `${name} · ${u.username ?? ''}`.replace(/ · $/, '') : String(u.username ?? ''));
      })
      .catch(() => undefined);
    fetchLinks()
      .then(() => alive && setState('ready'))
      .catch((e) => alive && setState(axios.isAxiosError(e) && e.response?.status === 404 ? 'unavailable' : 'error'))
      .finally(() => hideLoading());
    return () => {
      alive = false;
    };
  }, [open, fetchLinks, showLoading, hideLoading]);

  // Subtítulos: se descargan como texto y se pasan al <track> como blob (así el video no necesita CORS).
  const captionsSrc = links?.captions?.url ?? null;
  useEffect(() => {
    if (!captionsSrc) {
      setCaptionsUrl(null);
      return undefined;
    }
    let url: string | null = null;
    let alive = true;
    fetch(captionsSrc)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((text) => {
        if (!alive) return;
        url = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
        setCaptionsUrl(url);
      })
      .catch(() => alive && setCaptionsUrl(null));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [captionsSrc]);

  // Al cargar la pista, se muestra (los navegadores la dejan oculta aunque tenga `default`).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !captionsUrl) return;
    const show = () => Array.from(v.textTracks).forEach((tr) => (tr.mode = 'showing'));
    show();
    v.addEventListener('loadedmetadata', show);
    return () => v.removeEventListener('loadedmetadata', show);
  }, [captionsUrl, links]);

  // El enlace vence a los 5 minutos: si el video falla (por ejemplo al adelantar), se pide otro.
  const onVideoError = useCallback(() => {
    if (refreshing.current || !links) return;
    const expired = Date.parse(links.video.expiresAt) - Date.now() < 30_000;
    if (!expired) {
      setState('error');
      return;
    }
    refreshing.current = true;
    resumeAt.current = videoRef.current?.currentTime ?? 0;
    fetchLinks()
      .catch(() => setState('error'))
      .finally(() => {
        refreshing.current = false;
      });
  }, [links, fetchLinks]);

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (v && resumeAt.current != null) {
      v.currentTime = resumeAt.current;
      resumeAt.current = null;
      v.play().catch(() => undefined);
    }
  };

  // Pantalla completa del contenedor (con la marca de agua).
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === boxRef.current && !!boxRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const canFullscreen = typeof document !== 'undefined' && !!document.fullscreenEnabled;
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else boxRef.current?.requestFullscreen?.().catch(() => undefined);
  };

  const close = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    videoRef.current?.pause();
    onClose();
  };

  const lang = currentLang();
  const company = brand?.name || '';
  const dateText = new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(now);
  const watermark = [userName, company, dateText].filter(Boolean).join('\n');

  return (
    <StandardModal
      open={open}
      onClose={close}
      title={t('tours.video.heading')}
      subtitle={links?.title ? t('tours.video.subtitle', { title: links.title }) : undefined}
      maxWidth={1280}
      loading={ml.loading}
      actions={
        <>
          <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 220px', minWidth: 0 }}>
            {t('tours.video.privateNote')}
          </Typography>
          {canFullscreen && state === 'ready' && (
            <Button variant="outlined" startIcon={fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />} onClick={toggleFullscreen}>
              {fullscreen ? t('tours.video.exitFullscreen') : t('tours.video.fullscreen')}
            </Button>
          )}
        </>
      }
    >
      {state === 'unavailable' && <Alert severity="info">{t('tours.video.unavailable')}</Alert>}
      {state === 'error' && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { setState('loading'); fetchLinks().then(() => setState('ready')).catch(() => setState('error')); }}>
              {t('tours.video.retry')}
            </Button>
          }
        >
          {t('tours.video.error')}
        </Alert>
      )}
      {state === 'loading' && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('tours.video.loading')}
        </Typography>
      )}
      {state === 'ready' && links && (
        <Box
          ref={boxRef}
          onContextMenu={(e) => e.preventDefault()}
          sx={{
            position: 'relative',
            width: '100%',
            minWidth: 0,
            aspectRatio: '16 / 9',
            maxHeight: { xs: 'calc(100dvh - 190px)', sm: 'calc(90vh - 190px)' },
            bgcolor: 'common.black',
            borderRadius: { xs: 1, sm: 1.5 },
            overflow: 'hidden',
            '&:fullscreen': { maxHeight: 'none', borderRadius: 0, aspectRatio: 'auto', height: '100%' },
          }}
        >
          <Box
            component="video"
            ref={videoRef}
            key={links.video.url}
            src={links.video.url}
            controls
            autoPlay
            playsInline
            preload="metadata"
            disablePictureInPicture
            // Sin descarga ni pantalla completa propia del <video> (la del contenedor conserva la marca).
            {...({ controlsList: 'nodownload nofullscreen noremoteplayback', disableRemotePlayback: true } as Record<string, unknown>)}
            onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
            onError={onVideoError}
            onLoadedMetadata={onLoadedMetadata}
            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', bgcolor: 'common.black' }}
          >
            {captionsUrl && <track kind="subtitles" src={captionsUrl} srcLang={links.captions?.lang ?? lang} label={t('tours.video.captions')} default />}
          </Box>
          <Watermark text={watermark} />
        </Box>
      )}
    </StandardModal>
  );
};

export default ScreenVideoPlayer;
